// src/utils/registrationFillCurve.js
// Aggregated registration fill curves for admin dashboard (gross signups vs days before event).

import { getEventDate } from './eventUtils';

/** @typedef {{ d: number, c: number }} FillPoint */
/** @typedef {{ metric: string, points: FillPoint[], finalC: number, source: string, updatedAt?: *, skippedNoCreatedAt?: number }} RegistrationFillCurve */

export const REGISTRATION_FILL_METRIC = 'grossSignups';

export function getFlyerCreatedAtMs(flyer) {
  const ts = flyer && flyer.createdAt;
  if (!ts) return null;
  try {
    if (typeof ts.toDate === 'function') {
      const d = ts.toDate();
      return d instanceof Date ? d.getTime() : null;
    }
    if (typeof ts.seconds === 'number') {
      return ts.seconds * 1000;
    }
    if (typeof ts === 'number') {
      return ts;
    }
  } catch {
    // ignore
  }
  return null;
}

/** Excludes withdrawn/duplicate only; cancelled freshlings still count toward gross signups. */
export function isWithdrawnOrDuplicateFlyer(flyer) {
  const statusText = (flyer.status || '').toString().toLowerCase();
  const notesText = (flyer.notes || '').toString().toLowerCase();
  return (
    statusText.includes('withdrawn') ||
    statusText.includes('duplicate') ||
    notesText.includes('withdrawn') ||
    notesText.includes('duplicate')
  );
}

/** Local calendar midnight */
export function toLocalDayStart(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Whole days from registration day to event day (both local calendar).
 * Positive = registration before event on the calendar.
 */
export function daysBeforeEventDay(registrationDay, eventDay) {
  if (!registrationDay || !eventDay) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((eventDay.getTime() - registrationDay.getTime()) / msPerDay);
}

/**
 * Map month/day (no year) to a calendar day on or before that edition's Saturday.
 * Handles Nov/Dec → prior calendar year vs Jan–Mar → event year (Fresh Meet-style window).
 *
 * Registration season is capped at ~180 days before the event. M/D values that
 * fall just *after* the event Saturday (e.g. 03/22 for a 03/21 event) would
 * otherwise resolve to the prior calendar year at d ≈ 365 — the cap prevents that.
 *
 * @param {number} month 1-12
 * @param {number} day 1-31
 * @param {{ date?: * }} event edition anchor (Saturday date for that year)
 */
export const MAX_REG_DAYS_BEFORE = 180;

export function resolveSignupLocalDayForEvent(month, day, event) {
  const eventDay = getEventDate(event);
  if (!eventDay) return null;
  const m = Number(month);
  const d = Number(day);
  if (!Number.isFinite(m) || m < 1 || m > 12 || !Number.isFinite(d) || d < 1 || d > 31) {
    return null;
  }

  const eventT = eventDay.getTime();
  const windowStart = eventT - MAX_REG_DAYS_BEFORE * 86400000;
  const baseY = eventDay.getFullYear();

  let best = null;
  let bestT = -Infinity;
  for (let deltaY = -2; deltaY <= 1; deltaY += 1) {
    const y = baseY + deltaY;
    const cand = new Date(y, m - 1, d);
    if (Number.isNaN(cand.getTime())) continue;
    const local = toLocalDayStart(cand);
    const t = local.getTime();
    if (t <= eventT && t >= windowStart && t > bestT) {
      bestT = t;
      best = local;
    }
  }
  return best;
}

/**
 * Sorted cumulative totals by day → map of dayKey → **new** signups that day (non-negative deltas).
 * @param {Array<{ dayKey: string, cum: number }>} pairs sorted by dayKey ascending
 */
export function cumulativePairsToDailyMap(pairs) {
  const lastByDay = new Map();
  for (const p of pairs) {
    if (!p || !p.dayKey) continue;
    const cum = Number(p.cum);
    if (!Number.isFinite(cum)) continue;
    lastByDay.set(p.dayKey, cum);
  }
  const keys = Array.from(lastByDay.keys()).sort();
  let prev = 0;
  const out = new Map();
  for (const k of keys) {
    let cum = lastByDay.get(k);
    if (cum < prev) cum = prev;
    const delta = cum - prev;
    if (delta > 0) out.set(k, delta);
    prev = cum;
  }
  return out;
}

/**
 * @param {FillPoint[]} points sorted by d descending
 * @param {number} finalC
 * @param {number} dTarget days before event
 * @returns {number|null} cumulative count at dTarget (stepwise between knots — no linear fill between sparse samples)
 */
export function interpolateCumulativeAtD(points, finalC, dTarget) {
  if (!points || points.length === 0 || !Number.isFinite(finalC) || finalC <= 0) return null;
  if (!Number.isFinite(dTarget)) return null;
  const first = points[0];
  const last = points[points.length - 1];
  if (dTarget > first.d) return 0;
  if (dTarget <= last.d) return last.c;
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    if (dTarget > b.d && dTarget <= a.d) {
      return a.c;
    }
  }
  return last.c;
}

export function percentAtDaysBefore(points, finalC, dTarget) {
  const c = interpolateCumulativeAtD(points, finalC, dTarget);
  if (c == null) return null;
  return (c / finalC) * 100;
}

/**
 * @param {Date[]} registrationMoments
 * @param {{ date?: * }} event
 * @returns {{ points: FillPoint[], finalC: number, skippedAfterEvent: number }}
 */
export function buildCurveFromRegistrationDates(registrationMoments, event) {
  const eventDay = getEventDate(event);
  if (!eventDay) {
    return { points: [], finalC: 0, skippedAfterEvent: 0 };
  }

  const dayKeyToCount = new Map();
  let skippedAfterEvent = 0;

  for (const raw of registrationMoments) {
    const regStart = toLocalDayStart(raw);
    if (!regStart) continue;
    if (regStart.getTime() > eventDay.getTime()) {
      skippedAfterEvent += 1;
      continue;
    }
    const key = `${regStart.getFullYear()}-${String(regStart.getMonth() + 1).padStart(2, '0')}-${String(regStart.getDate()).padStart(2, '0')}`;
    dayKeyToCount.set(key, (dayKeyToCount.get(key) || 0) + 1);
  }

  const sortedKeys = Array.from(dayKeyToCount.keys()).sort();
  let cumulative = 0;
  /** @type {FillPoint[]} */
  const rawPoints = [];
  for (const key of sortedKeys) {
    cumulative += dayKeyToCount.get(key) || 0;
    const [y, m, d] = key.split('-').map((x) => parseInt(x, 10));
    const regDay = new Date(y, m - 1, d);
    const dBefore = daysBeforeEventDay(regDay, eventDay);
    if (dBefore == null || dBefore < 0) continue;
    rawPoints.push({ d: dBefore, c: cumulative });
  }

  rawPoints.sort((p, q) => q.d - p.d);
  const points = [];
  for (const p of rawPoints) {
    if (points.length && points[points.length - 1].d === p.d) {
      points[points.length - 1] = { d: p.d, c: Math.max(points[points.length - 1].c, p.c) };
    } else {
      points.push({ d: p.d, c: p.c });
    }
  }

  const finalC = points.length ? points[points.length - 1].c : 0;
  return { points, finalC, skippedAfterEvent };
}

/**
 * Build curve from per-calendar-day **new** registration counts (not cumulative).
 * @param {Map<string, number>|Record<string, number>} dayKeyToNewCount keys `YYYY-MM-DD` → count that day
 * @param {{ date?: * }} event
 */
export function buildCurveFromDailyDayCounts(dayKeyToNewCount, event) {
  const eventDay = getEventDate(event);
  if (!eventDay) {
    return { points: [], finalC: 0, skippedAfterEvent: 0 };
  }

  const map =
    dayKeyToNewCount instanceof Map
      ? dayKeyToNewCount
      : new Map(Object.entries(dayKeyToNewCount || {}));

  const sortedKeys = Array.from(map.keys()).sort();
  let cumulative = 0;
  let skippedAfterEvent = 0;
  /** @type {FillPoint[]} */
  const rawPoints = [];

  for (const key of sortedKeys) {
    const add = Number(map.get(key));
    if (!Number.isFinite(add) || add <= 0) continue;
    cumulative += add;
    const parts = key.split('-').map((x) => parseInt(x, 10));
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) continue;
    const [y, m, d] = parts;
    const regDay = new Date(y, m - 1, d);
    if (Number.isNaN(regDay.getTime())) continue;
    if (regDay.getTime() > eventDay.getTime()) {
      skippedAfterEvent += add;
      continue;
    }
    const dBefore = daysBeforeEventDay(regDay, eventDay);
    if (dBefore == null || dBefore < 0) continue;
    rawPoints.push({ d: dBefore, c: cumulative });
  }

  rawPoints.sort((p, q) => q.d - p.d);
  const points = [];
  for (const p of rawPoints) {
    if (points.length && points[points.length - 1].d === p.d) {
      points[points.length - 1] = { d: p.d, c: Math.max(points[points.length - 1].c, p.c) };
    } else {
      points.push({ d: p.d, c: p.c });
    }
  }

  const finalC = points.length ? points[points.length - 1].c : 0;
  return { points, finalC, skippedAfterEvent };
}

/**
 * Gross signups: freshlings with createdAt, not test, not withdrawn/duplicate; includes cancelled.
 * @param {object[]} flyers
 * @param {{ date?: * }} event
 */
export function buildCurveFromFlyers(flyers, event) {
  const moments = [];
  let skippedNoCreatedAt = 0;
  for (const f of flyers || []) {
    if (!f || f.type !== 'freshling' || f.test) continue;
    if (isWithdrawnOrDuplicateFlyer(f)) continue;
    const ms = getFlyerCreatedAtMs(f);
    if (ms == null) {
      skippedNoCreatedAt += 1;
      continue;
    }
    moments.push(new Date(ms));
  }
  const { points, finalC, skippedAfterEvent } = buildCurveFromRegistrationDates(moments, event);
  return { points, finalC, skippedNoCreatedAt, skippedAfterEvent };
}

export function median(nums) {
  const arr = nums.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (!arr.length) return null;
  const mid = Math.floor(arr.length / 2);
  if (arr.length % 2) return arr[mid];
  return (arr[mid - 1] + arr[mid]) / 2;
}

/**
 * @param {RegistrationFillCurve[]} curves
 * @param {number} d
 */
export function medianHistoricPercentAtD(curves, d) {
  const pcts = [];
  for (const curve of curves) {
    const pts = curve && curve.points;
    const fc = curve && curve.finalC;
    if (!pts || !pts.length || !Number.isFinite(fc) || fc <= 0) continue;
    const p = percentAtDaysBefore(pts, fc, d);
    if (p != null) pcts.push(p);
  }
  return median(pcts);
}

/**
 * @param {RegistrationFillCurve[]} curves
 * @param {number} d
 */
export function medianHistoricCountAtD(curves, d) {
  const counts = [];
  for (const curve of curves) {
    const pts = curve && curve.points;
    const fc = curve && curve.finalC;
    if (!pts || !pts.length || !Number.isFinite(fc) || fc <= 0) continue;
    const c = interpolateCumulativeAtD(pts, fc, d);
    if (c != null) counts.push(c);
  }
  return median(counts);
}
